import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import RPSBoard from './RPSBoard';
import {
  createInitialGameState,
  processChoice,
  startNextRound,
  getGameStatus,
  CHOICE_EMOJIS,
} from './logic';
import {
  subscribeToGame,
  updateGameState,
  updateGameStatus,
  requestRematch,
  resetGameForRematch,
  saveGameToHistory,
  updateMatchStats,
  deleteGame,
  checkBothRematchRequested,
} from '../../services/gameService';
import { addRecentOpponent } from '../../services/friendService';
import { leaveRoom } from '../../services/roomService';
import { database } from '../../services/firebase';
import { ref, update, serverTimestamp } from 'firebase/database';
import { safeString } from '../../utils/safeString';
import './RockPaperScissors.css';

const RockPaperScissors = ({ roomCode, roomData, user }) => {
  const navigate = useNavigate();
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rematchRequested, setRematchRequested] = useState(false);
  const [opponentRematchRequested, setOpponentRematchRequested] = useState(false);
  const [error, setError] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const confettiShownRef = useRef(false);
  const gameRecordedRef = useRef(false);

  // Validate props
  if (!roomCode || !roomData || !user) {
    return (
      <div className="rps-container">
        <div className="error-message">Missing required data. Please try again.</div>
      </div>
    );
  }

  const isHost = roomData.host && roomData.host.userId === user.uid;
  const opponent = isHost ? roomData.guest : roomData.host;

  // If opponent left, show waiting state
  if (!opponent) {
    const handleReturnToLobby = async () => {
      try {
        const roomRef = ref(database, `rooms/${roomCode}`);
        await update(roomRef, {
          status: 'waiting',
          gameSelected: null,
          lastActivity: serverTimestamp()
        });
        navigate(`/room/${roomCode}`);
      } catch (error) {
        console.error('Error returning to lobby:', error);
        navigate(`/room/${roomCode}`);
      }
    };

    return (
      <div className="rps-container">
        <div className="game-header">
          <h2>Rock Paper Scissors</h2>
        </div>
        <div className="player-waiting-state">
          <div className="waiting-spinner"></div>
          <h3>Player left the room</h3>
          <p>Waiting for player to return...</p>
          <p className="waiting-hint">The game will resume when they come back</p>
          <button className="leave-button" onClick={handleReturnToLobby}>
            Return to Lobby
          </button>
        </div>
      </div>
    );
  }

  // Subscribe to game updates
  useEffect(() => {
    try {
      const unsubscribe = subscribeToGame(roomCode, async (game) => {
        if (game && game.gameState) {
          setGameState(game.gameState);

          // Check rematch requests
          if (game.rematchRequests) {
            const myRequest = !!game.rematchRequests[user.uid];
            const opponentRequest = !!game.rematchRequests[opponent.userId];

            setRematchRequested(myRequest);
            setOpponentRematchRequested(opponentRequest);

            // If both players requested rematch, only the host resets the game
            if (myRequest && opponentRequest && isHost && game.gameState.isGameOver) {
              console.log('Both players requested rematch, host is resetting game...');

              const newGameState = createInitialGameState(
                roomData.host.userId,
                roomData.guest.userId
              );

              await resetGameForRematch(
                roomCode,
                'rps',
                newGameState,
                {
                  host: roomData.host,
                  guest: roomData.guest,
                }
              );

              setShowResult(false);
            }
          } else {
            setRematchRequested(false);
            setOpponentRematchRequested(false);
          }
        } else if (game === null) {
          setError('Game not found. It may not have been created yet.');
        } else if (game && !game.gameState) {
          setError('Game data is missing gameState. Please try restarting the game.');
        }

        setLoading(false);
      });

      return () => unsubscribe();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [roomCode, user.uid, opponent.userId, isHost, roomData]);

  // Show result when both players have chosen
  useEffect(() => {
    if (gameState && gameState.bothChosen && !showResult) {
      setShowResult(true);
    }
  }, [gameState, showResult]);

  // Trigger confetti on win (only once per game)
  useEffect(() => {
    if (gameState && gameState.isGameOver && !confettiShownRef.current) {
      const iWon = (isHost && gameState.winner === 'host') || (!isHost && gameState.winner === 'guest');

      if (iWon) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
        confettiShownRef.current = true;
      }
    }

    // Reset the flags when game restarts (not game over anymore)
    if (gameState && !gameState.isGameOver) {
      confettiShownRef.current = false;
      gameRecordedRef.current = false;
    }
  }, [gameState, isHost]);

  // Save game to history when it ends (only once)
  useEffect(() => {
    if (gameState && gameState.isGameOver && !gameRecordedRef.current) {
      gameRecordedRef.current = true;

      const iWon = (isHost && gameState.winner === 'host') || (!isHost && gameState.winner === 'guest');
      const result = iWon ? 'won' : 'lost';

      // Convert winner ('host' or 'guest') to user ID
      const winnerUserId = gameState.winner === 'host'
        ? roomData.host.userId
        : roomData.guest.userId;

      // Save to game history
      saveGameToHistory(user.uid, {
        gameType: 'rps',
        opponent: safeString(opponent?.displayName, 'Opponent'),
        opponentId: opponent.userId,
        result,
        winner: winnerUserId,
        roomCode,
      });

      // Update match stats
      updateMatchStats(user.uid, 'rps', result);

      // Add to recent opponents
      addRecentOpponent(user.uid, {
        userId: opponent.userId,
        displayName: safeString(opponent?.displayName, 'Opponent'),
        photoURL: safeString(opponent?.photoURL, ''),
        lastGame: 'rps',
        lastResult: result,
      });

      // Update game status
      updateGameStatus(roomCode, 'completed');
    }
  }, [gameState, isHost, user.uid, opponent, roomCode, roomData]);

  const handleChoice = async (choice) => {
    if (!gameState || gameState.isGameOver) return;

    // Check if player already chose
    const myChoice = isHost ? gameState.hostChoice : gameState.guestChoice;
    if (myChoice) return;

    // Process choice
    const newGameState = processChoice(gameState, user.uid, choice, roomData.host.userId);

    // Update Firebase
    await updateGameState(roomCode, newGameState);
  };

  const handleNextRound = async () => {
    if (!gameState) return;

    const newGameState = startNextRound(gameState);
    await updateGameState(roomCode, newGameState);
    setShowResult(false);
  };

  const handleRematch = async () => {
    // Just request the rematch - the subscription will handle the reset
    await requestRematch(roomCode, user.uid);
    // Local state will be updated by the subscription
  };

  const handleLeaveGame = async () => {
    try {
      // Update room status back to waiting and clear game selections
      const roomRef = ref(database, `rooms/${roomCode}`);
      await update(roomRef, {
        status: 'waiting',
        gameSelected: null,
        'host/gameSelection': null,
        'guest/gameSelection': null,
        lastActivity: serverTimestamp()
      });
      // Navigate back to room lobby
      navigate(`/room/${roomCode}`);
    } catch (error) {
      console.error('Error leaving game:', error);
      // Navigate to room lobby anyway
      navigate(`/room/${roomCode}`);
    }
  };

  if (loading) {
    return (
      <div className="rps-container">
        <div className="loading-message">
          <div className="spinner-large"></div>
          <p>Loading game...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rps-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={handleLeaveGame}>Back to Lobby</button>
        </div>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="rps-container">
        <div className="error-message">
          <p>Game not found or not yet created</p>
          <button onClick={handleLeaveGame}>Back to Lobby</button>
        </div>
      </div>
    );
  }

  const myChoice = isHost ? gameState.hostChoice : gameState.guestChoice;
  const opponentChoice = isHost ? gameState.guestChoice : gameState.hostChoice;
  const myScore = isHost ? gameState.hostScore : gameState.guestScore;
  const opponentScore = isHost ? gameState.guestScore : gameState.hostScore;
  const iWon = gameState.isGameOver && ((isHost && gameState.winner === 'host') || (!isHost && gameState.winner === 'guest'));
  const iLost = gameState.isGameOver && !iWon;

  return (
    <div className="rps-container">
      {/* Header */}
      <div className="game-header">
        <h1 className="game-title">✊ Rock Paper Scissors ✌️</h1>
      </div>

      {/* Score Board */}
      <div className="scoreboard">
        <div className="score-item">
          <img src={safeString(user?.photoURL, '')} alt={safeString(user?.displayName, 'You')} className="score-avatar" />
          <div className="score-info">
            <p className="score-name">You</p>
            <p className="score-value">{myScore}</p>
          </div>
        </div>

        <div className="score-divider">
          <p className="round-info">Round {gameState.currentRound}/{gameState.maxRounds}</p>
        </div>

        <div className="score-item">
          <img src={safeString(opponent?.photoURL, '')} alt={safeString(opponent?.displayName, 'Opponent')} className="score-avatar" />
          <div className="score-info">
            <p className="score-name">{safeString(opponent?.displayName, 'Opponent')}</p>
            <p className="score-value">{opponentScore}</p>
          </div>
        </div>
      </div>

      {/* Game Board */}
      {!gameState.isGameOver && (
        <RPSBoard
          myChoice={myChoice}
          opponentChoice={opponentChoice}
          bothChosen={gameState.bothChosen}
          showResult={showResult}
          onChoice={handleChoice}
          onNextRound={handleNextRound}
          opponent={opponent}
          isHost={isHost}
        />
      )}

      {/* Game Over */}
      {gameState.isGameOver && (
        <div className="game-over">
          <div className={`result-message ${iWon ? 'won' : 'lost'}`}>
            {iWon ? (
              <>
                <span className="result-icon">🎉</span>
                <h2>You Won!</h2>
                <p>Final Score: {myScore} - {opponentScore}</p>
              </>
            ) : (
              <>
                <span className="result-icon">😞</span>
                <h2>You Lost</h2>
                <p>Final Score: {myScore} - {opponentScore}</p>
              </>
            )}
          </div>

          {/* Round History */}
          <div className="round-history">
            <h3>Round History</h3>
            <div className="history-list">
              {gameState.rounds.map((round, index) => {
                const roundWinner = round.winner === 'player1' ? 'host' : round.winner === 'player2' ? 'guest' : 'tie';
                const iWonRound = (isHost && roundWinner === 'host') || (!isHost && roundWinner === 'guest');
                return (
                  <div key={index} className={`history-item ${roundWinner === 'tie' ? 'tie' : iWonRound ? 'won' : 'lost'}`}>
                    <span className="round-num">R{round.round}</span>
                    <span className="round-choices">
                      {CHOICE_EMOJIS[isHost ? round.hostChoice : round.guestChoice]} vs {CHOICE_EMOJIS[isHost ? round.guestChoice : round.hostChoice]}
                    </span>
                    <span className="round-result">
                      {roundWinner === 'tie' ? 'Tie' : iWonRound ? 'Won' : 'Lost'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rematch Section */}
          <div className="rematch-section">
            {!rematchRequested && !opponentRematchRequested && (
              <button className="rematch-button" onClick={handleRematch}>
                🔄 Request Rematch
              </button>
            )}

            {rematchRequested && !opponentRematchRequested && (
              <div className="rematch-status">
                <p>⏳ Waiting for {safeString(opponent?.displayName, 'Opponent')} to accept rematch...</p>
              </div>
            )}

            {!rematchRequested && opponentRematchRequested && (
              <div className="rematch-status">
                <p>🎮 {safeString(opponent?.displayName, 'Opponent')} wants a rematch!</p>
                <button className="rematch-button accept" onClick={handleRematch}>
                  ✓ Accept Rematch
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Leave Game Button - Always Visible */}
      <div className="leave-game-section">
        <button className="leave-button" onClick={handleLeaveGame}>
          Leave Game
        </button>
      </div>
    </div>
  );
};

export default RockPaperScissors;
